import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Check, FileDown, Loader2, Plus, RefreshCw, Save, Send, Sparkles, Trash2, X,
} from 'lucide-react'
import { useDeleteQuote, useQuote } from '../../../hooks/useQuotes.js'
import { useServiceCredentials } from '../../../hooks/useServiceCredentials.js'
import { getQuotePdfUrl } from '../../../services/api.js'
import { getSergeProjects } from '../../../services/sergeApi.js'
import { INVOICE_STATUS, QUOTE_STATUS, VAT_OPTIONS, formatEuros, inputClass, vatRate } from '../constants.js'
import PennylaneNotice from '../components/PennylaneNotice.jsx'

const EMPTY_LINE = { label: '', description: '', quantity: '1', unit: 'jour', unitPrice: '0', vatRate: 'FR_200', productId: null }
const CUSTOMER_FIELDS = [
  { key: 'name', label: 'Société', placeholder: 'Acme BTP', wide: true },
  { key: 'email', label: 'Email de facturation', placeholder: 'compta@acme.fr', wide: true },
  { key: 'address', label: 'Adresse', placeholder: '1 rue de la Paix', wide: true },
  { key: 'postalCode', label: 'Code postal', placeholder: '69001' },
  { key: 'city', label: 'Ville', placeholder: 'Lyon' },
  { key: 'country', label: 'Pays (code)', placeholder: 'FR' },
  { key: 'regNo', label: 'SIREN', placeholder: '123456789' },
  { key: 'vatNumber', label: 'N° TVA', placeholder: 'FR12345678901', wide: true },
]

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback

/** Copie modifiable d'un devis (les nombres en texte, le temps de la saisie). */
function toDraft(quote) {
  return {
    title: quote.title,
    specification: quote.specification ?? '',
    sergeProjectId: quote.sergeProjectId ?? '',
    validityDays: String(quote.validityDays),
    customer: Object.fromEntries(CUSTOMER_FIELDS.map((f) => [f.key, quote.customer?.[f.key] ?? ''])),
    lines: quote.lines.map((l) => ({ ...l, description: l.description ?? '', quantity: String(l.quantity), unitPrice: String(l.unitPrice) })),
  }
}

function toPayload(draft) {
  return {
    title: draft.title,
    specification: draft.specification,
    sergeProjectId: draft.sergeProjectId || null,
    validityDays: Number(draft.validityDays) || 30,
    customer: draft.customer,
    lines: draft.lines.map((l) => ({
      ...l,
      quantity: Number(String(l.quantity).replace(',', '.')),
      unitPrice: Number(String(l.unitPrice).replace(',', '.')),
    })),
  }
}

function totalsOf(lines) {
  let beforeTax = 0
  let tax = 0
  for (const line of lines) {
    const ht = (Number(String(line.quantity).replace(',', '.')) || 0) * (Number(String(line.unitPrice).replace(',', '.')) || 0)
    beforeTax += ht
    tax += (ht * vatRate(line.vatRate)) / 100
  }
  return { beforeTax, tax, withTax: beforeTax + tax }
}

/** Contrats > Devis > un devis : client, spécifications, proposition de l'agent, lignes, Pennylane. */
export default function QuoteEditorPage({ quoteId }) {
  const navigate = useNavigate()
  const q = useQuote(quoteId)
  const deleteQuote = useDeleteQuote()
  const { quote } = q
  const [draft, setDraft] = useState(null)
  const [instructions, setInstructions] = useState('')

  // Recharge le brouillon quand le devis change côté serveur (enregistrement, proposition appliquée).
  useEffect(() => {
    if (quote) setDraft(toDraft(quote))
  }, [quote?.updatedAt]) // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(
    () => Boolean(quote && draft) && JSON.stringify(toPayload(draft)) !== JSON.stringify(toPayload(toDraft(quote))),
    [quote, draft]
  )

  // Première proposition de l'agent : appliquée directement si le devis n'a encore aucune ligne.
  const generation = quote?.generation
  useEffect(() => {
    if (generation?.status === 'completed' && !generation.applied && quote.lines.length === 0 && quote.editable) {
      q.applyGeneration.mutate(quote.id, { onError: (e) => toast.error(errorMessage(e, 'Proposition non appliquée')) })
    }
  }, [generation?.id, generation?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading || !draft) {
    return (
      <div className="flex items-center justify-center py-20 text-white/50">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    )
  }
  if (q.isError || !quote) return <p className="text-sm text-red-300">Devis introuvable.</p>

  const editable = quote.editable
  const status = QUOTE_STATUS[quote.status] ?? QUOTE_STATUS.draft
  const totals = totalsOf(draft.lines)
  const pl = quote.pennylane

  const save = (onDone) =>
    q.update.mutate(
      { id: quote.id, ...toPayload(draft) },
      {
        onSuccess: () => (onDone ? onDone() : toast.success('Devis enregistré')),
        onError: (e) => toast.error(errorMessage(e, 'Enregistrement impossible')),
      }
    )
  // Les actions partent de la version enregistrée : on enregistre d'abord si besoin.
  const afterSave = (run) => (dirty ? save(run) : run())

  const runGenerate = () =>
    afterSave(() =>
      q.generate.mutate(
        { id: quote.id, instructions: instructions.trim() || null },
        {
          onSuccess: () => toast.success('L’agent prépare une proposition…'),
          onError: (e) => toast.error(errorMessage(e, 'Impossible de lancer l’agent')),
        }
      )
    )

  const runPush = () =>
    afterSave(() =>
      q.push.mutate(quote.id, {
        onSuccess: (updated) => toast.success(`Devis ${updated.pennylane.quoteNumber ?? ''} enregistré dans Pennylane`),
        onError: (e) => toast.error(errorMessage(e, 'Pennylane a refusé le devis')),
      })
    )

  const simple = (mutation, variables, success, failure) =>
    mutation.mutate(variables, {
      onSuccess: () => success && toast.success(success),
      onError: (e) => toast.error(errorMessage(e, failure)),
    })

  const openPdf = async (document) => {
    const tab = window.open('', '_blank')
    try {
      const { url } = await getQuotePdfUrl({ id: quote.id, document })
      if (tab) tab.location.href = url
      else window.location.href = url
    } catch (e) {
      tab?.close()
      toast.error(errorMessage(e, 'PDF indisponible'))
    }
  }

  const setLine = (index, field, value) =>
    setDraft((d) => ({ ...d, lines: d.lines.map((l, i) => (i === index ? { ...l, [field]: value } : l)) }))

  const handleDelete = () => {
    if (!window.confirm('Supprimer ce devis ?')) return
    deleteQuote.mutate(quote.id, {
      onSuccess: () => navigate('/dashboard/contrats/devis'),
      onError: (e) => toast.error(errorMessage(e, 'Suppression impossible')),
    })
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/dashboard/contrats/devis" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Tous les devis
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${status.tone}`}>{status.label}</span>
          {pl.quoteNumber && <span className="text-xs text-white/50">Pennylane {pl.quoteNumber}</span>}
          {editable && (
            <button
              type="button"
              onClick={() => save()}
              disabled={!dirty || q.update.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#5b5cf4] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            >
              {q.update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {dirty ? 'Enregistrer' : 'Enregistré'}
            </button>
          )}
        </div>
      </div>

      <input
        value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        disabled={!editable}
        className="w-full border-0 bg-transparent p-0 text-2xl font-bold text-white outline-none"
        placeholder="Titre du devis"
      />

      {!quote.editable && (
        <p className="m-0 text-sm text-white/50">Ce devis n’est plus modifiable : il a été accepté, refusé ou facturé.</p>
      )}

      <div className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <h3 className="m-0 text-base font-semibold text-white">Spécifications</h3>
          <textarea
            value={draft.specification}
            onChange={(e) => setDraft({ ...draft, specification: e.target.value })}
            disabled={!editable}
            rows={7}
            className={inputClass}
            placeholder="Décrivez le besoin du client : périmètre, fonctionnalités, délais, contraintes…"
          />
          {quote.offer ? (
            <OfferLinks
              quote={quote}
              editable={editable}
              onImport={() => afterSave(() => simple(q.importSerge, quote.id, 'Spécifications reprises de Serge', 'Serge n’a pas renvoyé de spécifications'))}
              importing={q.importSerge.isPending}
            />
          ) : (
            <SergeProjectSelect
              value={draft.sergeProjectId}
              onChange={(value) => setDraft({ ...draft, sergeProjectId: value })}
              disabled={!editable}
            />
          )}
          {editable && (
            <GenerationPanel
              quote={quote}
              instructions={instructions}
              setInstructions={setInstructions}
              onGenerate={runGenerate}
              generating={q.generate.isPending}
              onApply={() => simple(q.applyGeneration, quote.id, 'Proposition de l’agent appliquée', 'Proposition non appliquée')}
            />
          )}
        </section>

        <section className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <h3 className="m-0 text-base font-semibold text-white">Client</h3>
          <div className="grid grid-cols-2 gap-3">
            {CUSTOMER_FIELDS.map((field) => (
              <label key={field.key} className={`space-y-1 text-xs text-white/50 ${field.wide ? 'col-span-2' : ''}`}>
                <span>{field.label}</span>
                <input
                  value={draft.customer[field.key]}
                  onChange={(e) => setDraft({ ...draft, customer: { ...draft.customer, [field.key]: e.target.value } })}
                  disabled={!editable || Boolean(pl.quoteId)}
                  placeholder={field.placeholder}
                  className={inputClass}
                />
              </label>
            ))}
          </div>
          {pl.quoteId && <p className="m-0 text-xs text-white/40">Client créé dans Pennylane : modifiez-le dans Pennylane.</p>}
          <label className="block space-y-1 text-xs text-white/50">
            <span>Validité du devis (jours)</span>
            <input
              type="number"
              min={1}
              max={365}
              value={draft.validityDays}
              onChange={(e) => setDraft({ ...draft, validityDays: e.target.value })}
              disabled={!editable}
              className={`${inputClass} w-32`}
            />
          </label>
        </section>
      </div>

      <section className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="m-0 text-base font-semibold text-white">Lignes du devis</h3>
          {editable && (
            <button
              type="button"
              onClick={() => setDraft({ ...draft, lines: [...draft.lines, { ...EMPTY_LINE }] })}
              className="inline-flex items-center gap-1 rounded-xl border border-white/10 px-3 py-1.5 text-sm text-white/70 hover:text-white"
            >
              <Plus className="h-4 w-4" /> Ajouter une ligne
            </button>
          )}
        </div>

        {draft.lines.length === 0 ? (
          <p className="m-0 py-6 text-center text-sm text-white/40">
            Aucune ligne : lancez l’agent à partir des spécifications, ou ajoutez des lignes à la main.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-white/40">
                <tr>
                  <th className="pb-2 pr-2 font-semibold">Prestation</th>
                  <th className="w-20 pb-2 pr-2 font-semibold">Qté</th>
                  <th className="w-24 pb-2 pr-2 font-semibold">Unité</th>
                  <th className="w-28 pb-2 pr-2 font-semibold">Prix HT</th>
                  <th className="w-28 pb-2 pr-2 font-semibold">TVA</th>
                  <th className="w-28 pb-2 pr-2 text-right font-semibold">Total HT</th>
                  <th className="w-8 pb-2" />
                </tr>
              </thead>
              <tbody>
                {draft.lines.map((line, index) => {
                  const total = (Number(String(line.quantity).replace(',', '.')) || 0) * (Number(String(line.unitPrice).replace(',', '.')) || 0)
                  return (
                    <tr key={index} className="border-t border-white/5 align-top">
                      <td className="py-2 pr-2">
                        <input value={line.label} onChange={(e) => setLine(index, 'label', e.target.value)} disabled={!editable} className={inputClass} placeholder="Libellé" />
                        <textarea
                          value={line.description}
                          onChange={(e) => setLine(index, 'description', e.target.value)}
                          disabled={!editable}
                          rows={2}
                          className={`${inputClass} mt-1 text-xs`}
                          placeholder="Détail (facultatif)"
                        />
                        {line.productId && <span className="text-[11px] text-white/40">Produit du catalogue Pennylane</span>}
                      </td>
                      <td className="py-2 pr-2"><input value={line.quantity} onChange={(e) => setLine(index, 'quantity', e.target.value)} disabled={!editable} className={inputClass} inputMode="decimal" /></td>
                      <td className="py-2 pr-2"><input value={line.unit} onChange={(e) => setLine(index, 'unit', e.target.value)} disabled={!editable} className={inputClass} /></td>
                      <td className="py-2 pr-2"><input value={line.unitPrice} onChange={(e) => setLine(index, 'unitPrice', e.target.value)} disabled={!editable} className={inputClass} inputMode="decimal" /></td>
                      <td className="py-2 pr-2">
                        <select value={line.vatRate} onChange={(e) => setLine(index, 'vatRate', e.target.value)} disabled={!editable} className={inputClass}>
                          {VAT_OPTIONS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                        </select>
                      </td>
                      <td className="py-2 pr-2 pt-4 text-right text-white">{formatEuros(total)}</td>
                      <td className="py-2 pt-3">
                        {editable && (
                          <button
                            type="button"
                            onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, i) => i !== index) })}
                            className="text-white/30 hover:text-red-400"
                            aria-label="Supprimer la ligne"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="ml-auto w-full max-w-xs space-y-1 border-t border-white/10 pt-3 text-sm">
          <div className="flex justify-between text-white/60"><span>Total HT</span><span>{formatEuros(totals.beforeTax)}</span></div>
          <div className="flex justify-between text-white/60"><span>TVA</span><span>{formatEuros(totals.tax)}</span></div>
          <div className="flex justify-between text-base font-semibold text-white"><span>Total TTC</span><span>{formatEuros(totals.withTax)}</span></div>
        </div>

        {quote.pricingNotes && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/70">
            <p className="m-0 mb-1 flex items-center gap-2 text-sm font-semibold text-white"><Sparkles className="h-4 w-4 text-[#5b5cf4]" /> Chiffrage de l’agent</p>
            <p className="m-0 whitespace-pre-line text-sm">{quote.pricingNotes}</p>
          </div>
        )}
      </section>

      <PennylanePanel quote={quote} q={q} dirty={dirty} onPush={runPush} openPdf={openPdf} simple={simple} />

      {!pl.quoteId && (
        <button type="button" onClick={handleDelete} className="inline-flex items-center gap-2 text-sm text-red-300/80 hover:text-red-300">
          <Trash2 className="h-4 w-4" /> Supprimer ce devis
        </button>
      )}
    </div>
  )
}

/** Liens du devis : l'offre (projet Serge) dont il vient et le prospect, avec reprise des specs. */
function OfferLinks({ quote, editable, onImport, importing }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Link
        to={`/dashboard/serge-projects/${quote.offer.id}/analyse`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#5b5cf4]/30 bg-[#5b5cf4]/5 px-3 py-1 text-xs font-semibold text-[#5b5cf4] hover:border-[#5b5cf4]/60"
      >
        Offre : {quote.offer.name}
      </Link>
      {quote.prospect && (
        <Link
          to="/dashboard/crm/prospects"
          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs font-semibold text-white/80 hover:text-white"
        >
          Prospect : {[quote.prospect.name, quote.prospect.company].filter(Boolean).join(' · ')}
          {quote.prospect.source === 'linkedin' && <span className="text-[#4a9be8]">· LinkedIn</span>}
        </Link>
      )}
      {editable && (
        <button
          type="button"
          onClick={onImport}
          disabled={importing}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-xs text-white/60 hover:text-white disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${importing ? 'animate-spin' : ''}`} /> Reprendre les specs de Serge
        </button>
      )}
    </div>
  )
}

function SergeProjectSelect({ value, onChange, disabled }) {
  const { data } = useQuery({ queryKey: ['sergeProjects'], queryFn: getSergeProjects, staleTime: 60000, retry: false })
  const projects = Array.isArray(data) ? data : data?.projects ?? data?.['hydra:member'] ?? []

  return (
    <label className="block space-y-1 text-xs text-white/50">
      <span>Lier à une offre Serge (facultatif) : enregistrez, puis reprenez ses spécifications</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className={inputClass}>
        <option value="">Aucun</option>
        {projects.map((p) => <option key={p.id} value={String(p.id)}>{p.name || `Projet ${p.id}`}</option>)}
      </select>
    </label>
  )
}

function GenerationPanel({ quote, instructions, setInstructions, onGenerate, generating, onApply }) {
  const generation = quote.generation
  const running = ['queued', 'running'].includes(generation?.status)
  const proposal = generation?.status === 'completed' && !generation.applied && quote.lines.length > 0 ? generation.result : null

  return (
    <div className="space-y-3 border-t border-white/10 pt-3">
      {quote.lines.length > 0 && (
        <input
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          className={inputClass}
          placeholder="Consignes pour l’agent (facultatif) : « moins de jours de dev », « ajouter une formation »…"
        />
      )}
      <button
        type="button"
        onClick={onGenerate}
        disabled={generating || running}
        className="inline-flex items-center gap-2 rounded-xl border border-[#5b5cf4]/30 bg-[#5b5cf4]/10 px-4 py-2 text-sm font-semibold text-[#5b5cf4] disabled:opacity-50"
      >
        {generating || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {running ? 'L’agent prépare une proposition…' : quote.lines.length > 0 ? 'Regénérer avec l’agent' : 'Générer le devis avec l’agent'}
      </button>
      {running && (
        <p className="m-0 text-xs text-white/40">
          {generation.progress?.message || 'Lecture des spécifications, du catalogue et de vos factures Pennylane…'}
        </p>
      )}
      {generation?.status === 'failed' && (
        <p className="m-0 text-sm text-red-300">L’agent n’a pas abouti : {(generation.error || '').split('\n')[0]}</p>
      )}
      {proposal && (
        <div className="space-y-2 rounded-xl border border-[#5b5cf4]/30 bg-[#5b5cf4]/5 p-4 text-sm">
          <p className="m-0 text-sm font-semibold text-white">
            Nouvelle proposition : {proposal.lines?.length ?? 0} lignes, {formatEuros(proposal.totalBeforeTax)} HT
          </p>
          <p className="m-0 line-clamp-4 whitespace-pre-line text-sm text-white/60">{proposal.notes}</p>
          <button
            type="button"
            onClick={onApply}
            className="inline-flex items-center gap-2 rounded-xl bg-[#5b5cf4] px-3 py-1.5 text-sm font-semibold text-white"
          >
            <Check className="h-4 w-4" /> Remplacer les lignes actuelles
          </button>
        </div>
      )}
    </div>
  )
}

function PennylanePanel({ quote, q, dirty, onPush, openPdf, simple }) {
  const pl = quote.pennylane
  const busy = [q.push, q.sync, q.setStatus, q.createInvoice, q.finalizeInvoice].some((m) => m.isPending)
  const button = 'inline-flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm font-semibold text-white/80 hover:border-white/20 disabled:opacity-40'

  return (
    <section className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-base font-semibold text-white">Pennylane</h3>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-white/50" />}
      </div>

      <div className="flex flex-wrap gap-2">
        {quote.editable && (
          <button type="button" onClick={onPush} disabled={busy || quote.lines.length === 0} className="inline-flex items-center gap-2 rounded-xl bg-[#5b5cf4] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            <Send className="h-4 w-4" />
            {pl.quoteId ? (dirty ? 'Enregistrer et mettre à jour dans Pennylane' : 'Mettre à jour dans Pennylane') : 'Créer le devis dans Pennylane'}
          </button>
        )}
        {pl.quoteId && (
          <>
            <button type="button" onClick={() => openPdf('quote')} className={button}><FileDown className="h-4 w-4" /> PDF du devis</button>
            <button type="button" onClick={() => simple(q.sync, quote.id, 'Statut à jour', 'Lecture impossible')} disabled={busy} className={button}>
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
          </>
        )}
        {pl.quoteId && !pl.invoiceId && quote.status === 'pending' && (
          <>
            <button type="button" onClick={() => simple(q.setStatus, { id: quote.id, status: 'accepted' }, 'Devis accepté', 'Statut refusé par Pennylane')} disabled={busy} className={button}>
              <Check className="h-4 w-4 text-[#5b5cf4]" /> Accepté par le client
            </button>
            <button type="button" onClick={() => simple(q.setStatus, { id: quote.id, status: 'denied' }, 'Devis marqué refusé', 'Statut refusé par Pennylane')} disabled={busy} className={button}>
              <X className="h-4 w-4 text-red-300" /> Refusé
            </button>
          </>
        )}
        {pl.quoteId && !pl.invoiceId && quote.status === 'accepted' && (
          <button
            type="button"
            onClick={() => window.confirm('Créer la facture dans Pennylane à partir de ce devis ?') && simple(q.createInvoice, quote.id, 'Facture créée (brouillon) dans Pennylane', 'Facture non créée')}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-[#5b5cf4] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            Créer la facture
          </button>
        )}
      </div>

      {!pl.quoteId && (
        <p className="m-0 text-xs text-white/40">
          Pennylane numérote le devis, produit le PDF et peut l’envoyer au client. Vous pourrez encore le corriger ici
          et le mettre à jour tant que le client ne l’a pas accepté.
        </p>
      )}

      {pl.invoiceId && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
          <span className="font-semibold text-white">Facture {pl.invoiceNumber || '(brouillon)'}</span>
          <span className="text-white/50">{INVOICE_STATUS[pl.invoiceStatus] ?? pl.invoiceStatus ?? ''}</span>
          {pl.invoiceStatus === 'draft' && (
            <button
              type="button"
              onClick={() => window.confirm('Valider la facture ? Elle reçoit son numéro définitif et ne sera plus modifiable.') && simple(q.finalizeInvoice, quote.id, 'Facture validée', 'Validation refusée par Pennylane')}
              disabled={busy}
              className={button}
            >
              <Check className="h-4 w-4" /> Valider la facture
            </button>
          )}
          <button type="button" onClick={() => openPdf('invoice')} className={button}><FileDown className="h-4 w-4" /> PDF de la facture</button>
        </div>
      )}

      <PennylaneHint />
    </section>
  )
}

function PennylaneHint() {
  const { providers, isLoading } = useServiceCredentials()
  const pennylane = providers.find((p) => p.provider === 'pennylane')
  if (isLoading || pennylane?.status === 'connected') return null
  return <PennylaneNotice />
}
